import React from "react";
import {
  AbsoluteFill,
  Audio,
  Video,
  interpolate,
  spring,
  staticFile,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Flame,
  Heart,
  Search,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  XCircle,
  Zap,
} from "lucide-react";

// Exact Word-Level Timestamps from Whisper (Corrected Typos)
interface WordItem {
  text: string;
  startFrame: number;
  endFrame: number;
  highlightColor?: string;
}

interface LineItem {
  startFrame: number;
  endFrame: number;
  words: WordItem[];
}

const LINES: LineItem[] = [
  {
    startFrame: 0,
    endFrame: 27,
    words: [
      { text: "JANGAN", startFrame: 0, endFrame: 7, highlightColor: "#FF4B4B" },
      { text: "BILANG", startFrame: 7, endFrame: 16 },
      { text: "WANITA", startFrame: 16, endFrame: 27 },
    ],
  },
  {
    startFrame: 27,
    endFrame: 43,
    words: [
      { text: "MATRE!", startFrame: 27, endFrame: 43, highlightColor: "#FF4B4B" },
    ],
  },
  {
    startFrame: 43,
    endFrame: 61,
    words: [
      { text: "HANYA", startFrame: 43, endFrame: 48 },
      { text: "KARENA", startFrame: 48, endFrame: 56 },
      { text: "DIA", startFrame: 56, endFrame: 61 },
    ],
  },
  {
    startFrame: 61,
    endFrame: 86,
    words: [
      { text: "BUTUH", startFrame: 61, endFrame: 70 },
      { text: "UANG,", startFrame: 70, endFrame: 86, highlightColor: "#FFE500" },
    ],
  },
  {
    startFrame: 93,
    endFrame: 118,
    words: [
      { text: "HIDUP", startFrame: 93, endFrame: 103 },
      { text: "ITU", startFrame: 103, endFrame: 109 },
      { text: "BUTUH", startFrame: 109, endFrame: 118 },
    ],
  },
  {
    startFrame: 118,
    endFrame: 139,
    words: [
      { text: "BIAYA,", startFrame: 118, endFrame: 139, highlightColor: "#38BDF8" },
    ],
  },
  {
    startFrame: 139,
    endFrame: 175,
    words: [
      { text: "MAKAN", startFrame: 139, endFrame: 145 },
      { text: "BUTUH", startFrame: 145, endFrame: 157 },
      { text: "UANG,", startFrame: 157, endFrame: 175, highlightColor: "#FFE500" },
    ],
  },
  {
    startFrame: 176,
    endFrame: 196,
    words: [
      { text: "MASA", startFrame: 176, endFrame: 181 },
      { text: "DEPAN", startFrame: 181, endFrame: 191 },
      { text: "JUGA", startFrame: 191, endFrame: 196 },
    ],
  },
  {
    startFrame: 196,
    endFrame: 230,
    words: [
      { text: "BUTUH", startFrame: 196, endFrame: 204 },
      { text: "PERSIAPAN.", startFrame: 204, endFrame: 230, highlightColor: "#D8FF3E" },
    ],
  },
  {
    startFrame: 233,
    endFrame: 252,
    words: [
      { text: "WANITA", startFrame: 233, endFrame: 240 },
      { text: "MEMANG", startFrame: 240, endFrame: 245 },
      { text: "BISA", startFrame: 245, endFrame: 252 },
    ],
  },
  {
    startFrame: 252,
    endFrame: 283,
    words: [
      { text: "BAHAGIA", startFrame: 252, endFrame: 261, highlightColor: "#FF69B4" },
      { text: "KARENA", startFrame: 261, endFrame: 267 },
      { text: "CINTA,", startFrame: 267, endFrame: 283, highlightColor: "#FF69B4" },
    ],
  },
  {
    startFrame: 283,
    endFrame: 297,
    words: [
      { text: "TAPI", startFrame: 283, endFrame: 288 },
      { text: "DIA", startFrame: 288, endFrame: 292 },
      { text: "JUGA", startFrame: 292, endFrame: 297 },
    ],
  },
  {
    startFrame: 297,
    endFrame: 324,
    words: [
      { text: "BUTUH", startFrame: 297, endFrame: 303 },
      { text: "KEPASTIAN,", startFrame: 303, endFrame: 324, highlightColor: "#D8FF3E" },
    ],
  },
  {
    startFrame: 324,
    endFrame: 344,
    words: [
      { text: "TANGGUNG", startFrame: 324, endFrame: 331, highlightColor: "#38BDF8" },
      { text: "JAWAB", startFrame: 331, endFrame: 339, highlightColor: "#38BDF8" },
      { text: "DAN", startFrame: 339, endFrame: 344 },
    ],
  },
  {
    startFrame: 344,
    endFrame: 373,
    words: [
      { text: "KEHIDUPAN", startFrame: 344, endFrame: 355 },
      { text: "YANG", startFrame: 355, endFrame: 359 },
      { text: "LAYAK.", startFrame: 359, endFrame: 373, highlightColor: "#FFE500" },
    ],
  },
  {
    startFrame: 373,
    endFrame: 400,
    words: [
      { text: "CINTA", startFrame: 373, endFrame: 378, highlightColor: "#FF69B4" },
      { text: "ITU", startFrame: 378, endFrame: 382 },
      { text: "PENTING,", startFrame: 382, endFrame: 400, highlightColor: "#FFE500" },
    ],
  },
  {
    startFrame: 400,
    endFrame: 417,
    words: [
      { text: "TAPI", startFrame: 400, endFrame: 405 },
      { text: "JANGAN", startFrame: 405, endFrame: 410, highlightColor: "#FF4B4B" },
      { text: "SURUH", startFrame: 410, endFrame: 417 },
    ],
  },
  {
    startFrame: 417,
    endFrame: 442,
    words: [
      { text: "WANITA", startFrame: 417, endFrame: 426 },
      { text: "BERTAHAN", startFrame: 426, endFrame: 435 },
      { text: "HIDUP", startFrame: 435, endFrame: 442 },
    ],
  },
  {
    startFrame: 442,
    endFrame: 474,
    words: [
      { text: "CUMA", startFrame: 442, endFrame: 447 },
      { text: "DENGAN", startFrame: 447, endFrame: 451 },
      { text: "JANJI!", startFrame: 451, endFrame: 474, highlightColor: "#FF4B4B" },
    ],
  },
];

export const ClipperProRemix: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Spring Helper
  const sp = (delay: number, damping = 12, stiffness = 180) =>
    spring({
      frame: frame - delay,
      fps,
      config: { damping, stiffness, mass: 0.8 },
    });

  // ===========================================================================
  // 1. CLEAN STABLE CAMERA FRAMING (Zero Jitter / Zero Weird Shaking)
  // ===========================================================================
  // Professional clippers keep the framing stable and let subtitles & SFX lead
  // Optional very gentle cinematic slow creep (1.00 -> 1.03) across 15 seconds
  const cameraScale = interpolate(frame, [0, 474], [1.0, 1.03], {
    extrapolateRight: "clamp",
  });

  // Find active subtitle line
  const activeLine =
    LINES.find((l) => frame >= l.startFrame && frame < l.endFrame) ||
    LINES[LINES.length - 1];

  // Duration progress (0 to 474 frames)
  const durationProgress = interpolate(frame, [0, 474], [0, 100], {
    extrapolateRight: "clamp",
  });

  return (
    <AbsoluteFill
      style={{
        backgroundColor: "#000000",
        overflow: "hidden",
        fontFamily: "'Anton', sans-serif",
      }}
    >
      {/* ======================================================================= */}
      {/* 2. BASE VIDEO (ROCK SOLID & STABLE FRAMING — ZERO SHAKING/JITTER) */}
      {/* ======================================================================= */}
      <div
        style={{
          width: "100%",
          height: "100%",
          transform: `scale(${cameraScale})`,
          transformOrigin: "center center",
        }}
      >
        <Video
          src={staticFile("clipper_base_1080.mp4")}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </div>

      {/* Cinematic Edge Vignette to guarantee caption contrast */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "radial-gradient(ellipse at center, rgba(0,0,0,0) 45%, rgba(0,0,0,0.65) 100%), linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 20%, transparent 65%, rgba(0,0,0,0.85) 100%)",
          pointerEvents: "none",
        }}
      />

      {/* ======================================================================= */}
      {/* 3. AUDIO SOUND DESIGN */}
      {/* ======================================================================= */}
      {/* Subtle Emotional Piano / Lo-fi BGM */}
      <Audio src={staticFile("bgm-clean.mp3")} volume={0.065} loop />

      {/* SFX Hit Cues */}
      {frame === 2 && <Audio src={staticFile("ui-pop.mp3")} volume={0.6} />}
      {frame === 27 && <Audio src={staticFile("sfx_error_buzzer.mp3")} volume={0.4} />}
      {frame === 70 && <Audio src={staticFile("sfx_cash_register.mp3")} volume={0.45} />}
      {frame === 118 && <Audio src={staticFile("pop.mp3")} volume={0.5} />}
      {frame === 157 && <Audio src={staticFile("sfx_cash_register.mp3")} volume={0.35} />}
      {frame === 204 && <Audio src={staticFile("chime.mp3")} volume={0.55} />}
      {frame === 252 && <Audio src={staticFile("chime.mp3")} volume={0.65} />}
      {frame === 297 && <Audio src={staticFile("ui-success.mp3")} volume={0.5} />}
      {frame === 324 && <Audio src={staticFile("sfx_level_up.mp3")} volume={0.55} />}
      {frame === 405 && <Audio src={staticFile("whoosh.mp3")} volume={0.5} />}
      {frame === 451 && <Audio src={staticFile("sfx_error_buzzer.mp3")} volume={0.4} />}

      {/* ======================================================================= */}
      {/* 4. TOP PROGRESS DURATION BAR */}
      {/* ======================================================================= */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 8,
          backgroundColor: "rgba(255, 255, 255, 0.15)",
          zIndex: 100,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${durationProgress}%`,
            backgroundColor: "#FFE500",
            boxShadow: "0 0 14px #FFE500",
          }}
        />
      </div>

      {/* ======================================================================= */}
      {/* 5. TOP HOOK HEADLINE BANNER (TikTok Safe Zone: Y = 135px) */}
      {/* ======================================================================= */}
      <div
        style={{
          position: "absolute",
          top: 135,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          zIndex: 100,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 12,
            backgroundColor: "rgba(10, 10, 10, 0.88)",
            backdropFilter: "blur(12px)",
            padding: "12px 28px",
            borderRadius: 24,
            border: "2px solid rgba(255, 229, 0, 0.4)",
            boxShadow: "0 10px 25px rgba(0,0,0,0.6), 4px 4px 0px #000000",
          }}
        >
          <Flame size={24} color="#FFE500" />
          <span
            style={{
              fontFamily: "'Anton', sans-serif",
              fontSize: 26,
              letterSpacing: "0.06em",
              color: "#FFFFFF",
              textTransform: "uppercase",
            }}
          >
            REALITA: <span style={{ color: "#FFE500" }}>BUKAN MATRE</span> TAPI
            REALISTIS
          </span>
        </div>
      </div>

      {/* ======================================================================= */}
      {/* 6. CONTEXTUAL FLOATING BADGES (POP-UP HIGHLIGHTS) */}
      {/* ======================================================================= */}
      {/* Badge 1: MATRE! (Frames 27 - 45) */}
      {frame >= 27 && frame < 45 && (
        <div
          style={{
            position: "absolute",
            top: 1020,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 90,
          }}
        >
          <div
            style={{
              transform: `scale(${sp(27)}) rotate(-4deg)`,
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              backgroundColor: "#FF3B30",
              color: "#FFFFFF",
              padding: "12px 28px",
              borderRadius: 20,
              border: "3px solid #000000",
              boxShadow: "6px 6px 0px #000000",
            }}
          >
            <ShieldAlert size={36} color="#FFFFFF" />
            <span style={{ fontSize: 32, letterSpacing: "0.04em" }}>
              BUKAN MATRE!
            </span>
          </div>
        </div>
      )}

      {/* Badge 2: BUTUH UANG (Frames 61 - 85) */}
      {frame >= 61 && frame < 85 && (
        <div
          style={{
            position: "absolute",
            top: 1020,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 90,
          }}
        >
          <div
            style={{
              transform: `scale(${sp(61)}) rotate(3deg)`,
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              backgroundColor: "#FFE500",
              color: "#000000",
              padding: "12px 28px",
              borderRadius: 20,
              border: "3px solid #000000",
              boxShadow: "6px 6px 0px #000000",
            }}
          >
            <Zap size={36} color="#000000" />
            <span style={{ fontSize: 32, letterSpacing: "0.04em" }}>
              SEMUA BUTUH UANG
            </span>
          </div>
        </div>
      )}

      {/* Badge 3: BIAYA HIDUP (Frames 118 - 145) */}
      {frame >= 118 && frame < 145 && (
        <div
          style={{
            position: "absolute",
            top: 1020,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 90,
          }}
        >
          <div
            style={{
              transform: `scale(${sp(118)}) rotate(-3deg)`,
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              backgroundColor: "#38BDF8",
              color: "#000000",
              padding: "12px 28px",
              borderRadius: 20,
              border: "3px solid #000000",
              boxShadow: "6px 6px 0px #000000",
            }}
          >
            <TrendingUp size={36} color="#000000" />
            <span style={{ fontSize: 32, letterSpacing: "0.04em" }}>
              BIAYA HIDUP ITU NYATA
            </span>
          </div>
        </div>
      )}

      {/* Badge 4: BAHAGIA KARENA CINTA (Frames 252 - 280) */}
      {frame >= 252 && frame < 280 && (
        <div
          style={{
            position: "absolute",
            top: 1020,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 90,
          }}
        >
          <div
            style={{
              transform: `scale(${sp(252)}) rotate(2deg)`,
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              backgroundColor: "#FF69B4",
              color: "#FFFFFF",
              padding: "12px 28px",
              borderRadius: 20,
              border: "3px solid #000000",
              boxShadow: "6px 6px 0px #000000",
            }}
          >
            <Heart size={36} color="#FFFFFF" fill="#FFFFFF" />
            <span style={{ fontSize: 32, letterSpacing: "0.04em" }}>
              CINTA ITU PENTING
            </span>
          </div>
        </div>
      )}

      {/* Badge 5: KEPASTIAN & TANGGUNG JAWAB (Frames 297 - 355) */}
      {frame >= 297 && frame < 355 && (
        <div
          style={{
            position: "absolute",
            top: 1020,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 90,
          }}
        >
          <div
            style={{
              transform: `scale(${sp(297)}) rotate(-2deg)`,
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              backgroundColor: "#D8FF3E",
              color: "#000000",
              padding: "12px 28px",
              borderRadius: 20,
              border: "3px solid #000000",
              boxShadow: "6px 6px 0px #000000",
            }}
          >
            <ShieldCheck size={38} color="#000000" />
            <span style={{ fontSize: 32, letterSpacing: "0.04em" }}>
              KEPASTIAN & TANGGUNG JAWAB!
            </span>
          </div>
        </div>
      )}

      {/* Badge 6: GAK BISA MAKAN JANJI (Frames 430 - 474) */}
      {frame >= 430 && (
        <div
          style={{
            position: "absolute",
            top: 1020,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            zIndex: 90,
          }}
        >
          <div
            style={{
              transform: `scale(${sp(430)}) rotate(3deg)`,
              display: "inline-flex",
              alignItems: "center",
              gap: 12,
              backgroundColor: "#FF3B30",
              color: "#FFFFFF",
              padding: "12px 28px",
              borderRadius: 20,
              border: "3px solid #000000",
              boxShadow: "6px 6px 0px #000000",
            }}
          >
            <XCircle size={38} color="#FFFFFF" />
            <span style={{ fontSize: 32, letterSpacing: "0.04em" }}>
              GAK BISA MAKAN JANJI!
            </span>
          </div>
        </div>
      )}

      {/* ======================================================================= */}
      {/* 7. HORMOZI WORD-BY-WORD BOUNCING POP SUBTITLE ENGINE */}
      {/* ======================================================================= */}
      <div
        style={{
          position: "absolute",
          top: 1140,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 100,
          padding: "0 50px",
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px 24px",
            maxWidth: 980,
            textAlign: "center",
          }}
        >
          {activeLine &&
            activeLine.words.map((word, idx) => {
              const isActive =
                frame >= word.startFrame && frame < word.endFrame;

              // Pop bounce spring on active word
              const pop = spring({
                frame: frame - word.startFrame,
                fps,
                config: { damping: 9, stiffness: 220, mass: 0.6 },
              });

              // Color determination
              let wordColor = "#FFFFFF";
              if (isActive) {
                wordColor = word.highlightColor || "#FFE500";
              }

              // Transform determination
              const wordTransform = isActive
                ? `scale(${1.0 + pop * 0.22}) rotate(-2.5deg)`
                : "scale(1.0) rotate(0deg)";

              return (
                <span
                  key={idx}
                  style={{
                    fontFamily: "'Anton', sans-serif",
                    fontSize: 78,
                    lineHeight: 1.05,
                    letterSpacing: "0.02em",
                    textTransform: "uppercase",
                    color: wordColor,
                    transform: wordTransform,
                    display: "inline-block",
                    WebkitTextStroke: "12px #000000",
                    paintOrder: "stroke fill",
                    textShadow: isActive
                      ? `0 0 25px ${wordColor}88, 8px 8px 0px #000000`
                      : "8px 8px 0px #000000",
                    transition: "transform 0.08s ease, color 0.08s ease",
                  }}
                >
                  {word.text}
                </span>
              );
            })}
        </div>
      </div>

      {/* ======================================================================= */}
      {/* 8. WATERMARK PILL ANCHORED BELOW SUBTITLE */}
      {/* ======================================================================= */}
      <div
        style={{
          position: "absolute",
          bottom: 240,
          left: 0,
          right: 0,
          display: "flex",
          justifyContent: "center",
          zIndex: 90,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            backgroundColor: "rgba(10, 10, 10, 0.7)",
            backdropFilter: "blur(8px)",
            padding: "8px 22px",
            borderRadius: 20,
            border: "1px solid rgba(255, 255, 255, 0.16)",
          }}
        >
          <span
            style={{
              fontFamily: "'Rajdhani', sans-serif",
              fontSize: 22,
              fontWeight: 800,
              letterSpacing: "0.08em",
              color: "rgba(255, 255, 255, 0.75)",
              textTransform: "uppercase",
            }}
          >
            TikTok @kyuidn.co
          </span>
        </div>
      </div>
    </AbsoluteFill>
  );
};
